/** @jsxImportSource @emotion/react */
import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { FaEdit, FaUpload, FaTrash } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ToastContainer, toast } from 'react-toastify';

const Container = styled.div`
  padding: 30px;
  background-color: #f8f9fb;
  min-height: 100vh;
  display: flex;
  justify-content: center;
  align-items: start;
`;

const FormCard = styled.div`
  background: #fff;
  padding: 30px;
  border-radius: 10px;
  box-shadow: 0 0 10px rgba(0, 0, 0, 0.05);
  width: 100%;
  max-width: 900px;
`;

const Heading = styled.h2`
  font-size: 20px;
  font-weight: 700;
  margin-bottom: 25px;
  color: #2B3497;
`;

const FormRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 20px;
  margin-bottom: 20px;
`;

const InputGroup = styled.div`
  flex: 1;
  min-width: 220px;

  label {
    font-size: 14px;
    font-weight: 500;
    margin-bottom: 4px;
  }

  input,
  select,
  textarea {
    width: 100%;
    padding: 8px;
    border: none;
    border-bottom: 1px solid #2b3497;
    outline: none;
    background: transparent;
  }

  textarea {
    resize: vertical;
  }
`;

const CheckboxGroup = styled.div`
  margin-bottom: 20px;

  label {
    margin-left: 8px;
  }
`;

const ButtonRow = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 10px;
  margin-top: 20px;

  button {
    padding: 10px 20px;
    font-weight: 600;
    border: none;
    border-radius: 4px;
    cursor: pointer;
  }

  .cancel {
    background: red;
    color: #fff;
  }

  .submit {
    background: #293495;
    color: #fff;
  }
`;

const UploadWrapper = styled.div`
  display: flex;
  flex-direction: column;
`;

const UploadBox = styled.label`
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #dcdcdc;
  border-radius: 6px;
  padding: 0 16px;
  font-size: 14px;
  color: #293495;
  cursor: pointer;
  background: #fff;
  height: 42px;
  margin-top: 4px;
  white-space: nowrap;

  svg {
    margin-right: 8px;
  }

  input {
    display: none;
  }
`;

const MaterialBox = styled.div`
  border: 1px solid #dcdcdc;
  border-radius: 8px;
  padding: 15px;
  margin-bottom: 20px;
`;

const MaterialHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
`;

const MaterialTitle = styled.label`
  font-weight: 600;
`;

const MaterialButton = styled.button`
  padding: 6px 14px;
  background-color: #fff;
  border: 1px solid #2b3497;
  color: #2b3497;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: 0.2s;
  &:hover {
    background-color: #f3f6fd;
  }
`;

const MaterialCard = styled.div`
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 12px 15px;
  margin-bottom: 10px;
`;

const MaterialCardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  font-weight: 600;
  margin-bottom: 10px;
`;

const MaterialActions = styled.div`
  display: flex;
  gap: 10px;
  align-items: center;
`;

const MaterialCardBody = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
  font-size: 14px;
  gap: 10px;
`;

const EmptyMaterialMessage = styled.div`
  text-align: center;
  color: #999;
  padding: 40px 0;
  font-size: 14px;
`;

const ModalBackdrop = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 999;
`;

const ModalContent = styled.div`
  background: #fff;
  padding: 30px;
  border-radius: 10px;
  max-width: 600px;
  width: 100%;
`;

export default function CreateTransactionForm() {
  const [showMaterialModal, setShowMaterialModal] = useState(false);
  const [editingIndex, setEditingIndex] = useState(null);
  const [materials, setMaterials] = useState([]);
  const [conditionOptions, setConditionOptions] = useState([]);
  const [unitOptions, setUnitOptions] = useState([]);
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    movementType: 'Inward',
    movementCategory: 'New',
    documentType: '',
    handledBy: '',
    fromAddress: '',
    toAddress: '',
    isReturnable: false,
    approvedBy: '',
    remarks: '',
    dueDate: new Date().toISOString().split('T')[0], // today's date in YYYY-MM-DD
    returnDate: new Date().toISOString().split('T')[0], // ✅ add this

  });

  const [materialInput, setMaterialInput] = useState({
    name: '',
    description: '',
    make: '',
    model: '',
    quantity: '1',
    unit: '',
    condition: '',
    value: '0',
  });

  useEffect(() => {
    if (showMaterialModal) {
      setMaterialInput({
        name: '',
        description: '',
        make: '',
        model: '',
        quantity: '1', // ✅ Reset here too
        unit: '',
        condition: '',
        value: '0'
      });
    }
  }, [showMaterialModal]);


  const handleMaterialChange = (e) => {
    const { name, value } = e.target;
    setMaterialInput((prev) => ({ ...prev, [name]: value }));
  };

  const handleSaveMaterial = () => {
    if (editingIndex !== null) {
      const updatedMaterials = [...materials];
      updatedMaterials[editingIndex] = materialInput;
      setMaterials(updatedMaterials);
      setEditingIndex(null);
    } else {
      setMaterials([...materials, materialInput]);
    }
    setMaterialInput({
      name: '',
      description: '',
      make: '',
      model: '',
      quantity: '',
      unit: '',
      condition: '',
      value: '',
    });
    setShowMaterialModal(false);
  };

  const handleEditMaterial = (index) => {
    setEditingIndex(index);
    setMaterialInput(materials[index]);
    setShowMaterialModal(true);
  };

  const handleDeleteMaterial = (index) => {
    const updatedMaterials = materials.filter((_, i) => i !== index);
    setMaterials(updatedMaterials);
  };
  const isAddDisabled = !(
    materialInput.name &&
    materialInput.quantity &&
    materialInput.unit &&
    materialInput.condition
  );

  const handleCancel = () => {
    navigate('/layout/MaterialManagement/MaterialTransaction');
  };

  useEffect(() => {
    const fetchConditionOptions = async () => {
      try {
        const response = await axios('StatusList/GetStatusListByType?type=condition');
        setConditionOptions(response.data);
      } catch (error) {
        console.error('Failed to fetch condition options:', error);
      }
    };
    const fetchUnitOptions = async () => {
      try {
        const response = await axios('StatusList/GetStatusListByType?type=unit');
        setUnitOptions(response.data);
      } catch (error) {
        console.error('Failed to fetch unit options:', error);
      }
    };

    fetchUnitOptions();
    fetchConditionOptions();
  }, []);

  const handleSubmit = async () => {
    const requiredFields = ['movementType', 'movementCategory', 'documentType', 'handledBy', 'fromAddress', 'toAddress', 'dueDate', 'approvedBy'];
    for (const field of requiredFields) {
      if (!formData[field]) {
        toast.error(`Please fill the all field`);
        return;
      }
    }

    if (materials.length === 0) {
      toast.error('Please add at least one material');
      return;
    }

    const payload = {
      ...formData,
      createdBy: 'Admin',
      materials: materials.map((m) => ({
        name: m.name,
        description: m.description,
        make: m.make,
        model: m.model,
        quantity: Number(m.quantity),
        unit: m.unit,
        condition: m.condition,
        approximateValue: m.value,
      })),
    };

    try {
      await axios.post('MaterialTransaction', payload);
      toast.success('Transaction Created Successfully!');
      setTimeout(() => {
        navigate('/layout/MaterialManagement/MaterialTransaction');
      }, 2000);
    } catch (error) {
      console.error('Create transaction failed:', error);
      toast.error('Failed to create transaction');
    }
  };


  return (
    <Container>
      <FormCard>
        <Heading>Create Transaction</Heading>

        <FormRow>
          <InputGroup>
            <label>Date *</label>
            <input type="date" value={formData.dueDate} onChange={(e) => setFormData({ ...formData, dueDate: e.target.value })} />
          </InputGroup>
          <InputGroup>
            <label>Movement *</label>
            <select value={formData.movementType} onChange={(e) => setFormData({ ...formData, movementType: e.target.value })}>
              <option>Inward</option>
              <option>Outward</option>
            </select>
          </InputGroup>
          <InputGroup>
            <label>Type *</label>
            <select value={formData.movementCategory} onChange={(e) => setFormData({ ...formData, movementCategory: e.target.value })}>
              <option>New</option>
              <option>Demo</option>
              <option>RMA</option>
              <option>Scrap</option>
            </select>
          </InputGroup>
        </FormRow>

        <FormRow>
          <InputGroup>
            <label>From Address *</label>
            <input type="text" value={formData.fromAddress} onChange={(e) => setFormData({ ...formData, fromAddress: e.target.value })} />
          </InputGroup>
          <InputGroup>
            <label>To Address *</label>
            <input type="text" value={formData.toAddress} onChange={(e) => setFormData({ ...formData, toAddress: e.target.value })} />
          </InputGroup>
        </FormRow>

        <CheckboxGroup>
          <input type="checkbox" id="returnable" checked={formData.isReturnable} onChange={(e) => setFormData({ ...formData, isReturnable: e.target.checked })} />
          <label htmlFor="returnable">Returnable</label>
        </CheckboxGroup>
        {formData.isReturnable && (
          <FormRow>
            <InputGroup>
              <label>Return Date *</label>
              <input
                type="date"
                value={formData.returnDate}
                onChange={(e) => setFormData({ ...formData, returnDate: e.target.value })}
              />
            </InputGroup>
          </FormRow>
        )}


        <MaterialBox>
          <MaterialHeader>
            <MaterialTitle>Material Details</MaterialTitle>
            <MaterialButton onClick={() => {
              setEditingIndex(null);
              setMaterialInput({ name: '', description: '', make: '', model: '', quantity: '', unit: '', condition: '', value: '' });
              setShowMaterialModal(true);
            }}>
              Add Material
            </MaterialButton>
          </MaterialHeader>

          {materials.length === 0 ? (
            <EmptyMaterialMessage>
              No materials added yet. Click "Add Material" to begin.
            </EmptyMaterialMessage>
          ) : (
            materials.map((item, index) => (
              <MaterialCard key={index}>
                <MaterialCardHeader>
                  {item.name}
                  <MaterialActions>
                    <FaEdit style={{ cursor: 'pointer' }} onClick={() => handleEditMaterial(index)} />
                    <FaTrash style={{ cursor: 'pointer', color: 'red' }} onClick={() => handleDeleteMaterial(index)} />
                  </MaterialActions>
                </MaterialCardHeader>
                <MaterialCardBody>
                  <div><strong>Description</strong><br />{item.description}</div>
                  <div><strong>Make/Model</strong><br />{`${item.make} ${item.model}`}</div>
                  <div><strong>Quantity</strong><br />{`${item.quantity} ${item.unit}`}</div>
                  <div><strong>Condition</strong><br />{item.condition}</div>
                </MaterialCardBody>
              </MaterialCard>
            ))
          )}
        </MaterialBox>

        <FormRow>
          <InputGroup>
            <label>Approved By *</label>
            <input type="text" value={formData.approvedBy} onChange={(e) => setFormData({ ...formData, approvedBy: e.target.value })} />
          </InputGroup>
          <InputGroup>
            <label>Handled By *</label>
            <input type="text" value={formData.handledBy} onChange={(e) => setFormData({ ...formData, handledBy: e.target.value })} />
          </InputGroup>
        </FormRow>

        <InputGroup style={{ marginBottom: '20px' }}>
          <label>Remarks</label>
          <textarea rows={2} value={formData.remarks} onChange={(e) => setFormData({ ...formData, remarks: e.target.value })}></textarea>
        </InputGroup>

        <FormRow>
          <InputGroup>
            <label>Document Type *</label>
            <select value={formData.documentType} onChange={(e) => setFormData({ ...formData, documentType: e.target.value })}>
              <option value="">Select document type</option>
              <option>WO</option>
              <option>PO</option>
              <option>GRN</option>
              <option>DC</option>
            </select>
          </InputGroup>
          <InputGroup>
            <UploadWrapper>
              <label>Documentation</label>
              <UploadBox>
                <FaUpload /> Upload Files (PDF, CSV, XLSX)
                <input type="file" accept=".pdf,.csv,.xlsx" />
              </UploadBox>
            </UploadWrapper>
          </InputGroup>
        </FormRow>

        <ButtonRow>
          <button className="cancel" onClick={handleCancel}>Cancel</button>
          <button className="submit" onClick={handleSubmit}>Create Transaction</button>
        </ButtonRow>
      </FormCard>

      {showMaterialModal && (
        <ModalBackdrop>
          <ModalContent>
            <h3 style={{ marginBottom: '20px', color: '#2B3497' }}>Add Material</h3>
            <FormRow>
              <InputGroup>
                <label>Name *</label>
                <input type="text" name="name" value={materialInput.name} placeholder='Material Name' onChange={handleMaterialChange} />
              </InputGroup>
              <InputGroup>
                <label>Description</label>
                <input type="text" name="description" value={materialInput.description} placeholder='Material Description' onChange={handleMaterialChange} />
              </InputGroup>
            </FormRow>
            <FormRow>
              <InputGroup>
                <label>Make</label>
                <input type="text" name="make" value={materialInput.make} placeholder='Manufacturer' onChange={handleMaterialChange} />
              </InputGroup>
              <InputGroup>
                <label>Model</label>
                <input type="text" name="model" value={materialInput.model} placeholder='Model Number' onChange={handleMaterialChange} />
              </InputGroup>
              <InputGroup>
                <label>Quantity *</label>
                <input
                  type="number"
                  name="quantity"
                  min="1"
                  step="1"
                  value={materialInput.quantity}
                  onChange={(e) =>
                    setMaterialInput((prev) => ({ ...prev, quantity: e.target.value }))
                  }
                  onKeyDown={(e) => {
                    if (e.key === 'e' || e.key === 'E' || e.key === '+' || e.key === '-') {
                      e.preventDefault(); // block scientific notation, plus, minus
                    }
                  }}
                  onInput={(e) => {
                    // prevent leading 0 (HTML doesn't prevent it, so we clean it here)
                    if (e.target.value.startsWith('0')) {
                      e.target.value = e.target.value.replace(/^0+/, '') || '1';
                      setMaterialInput((prev) => ({ ...prev, quantity: e.target.value }));
                    }
                  }}
                />
              </InputGroup>
            </FormRow>
            <FormRow>
              <InputGroup>
                <label>Unit *</label>
                <select name="unit" value={materialInput.unit} onChange={handleMaterialChange}>
                  <option value="">Select unit</option>
                  {unitOptions.map((option) => (
                    <option key={option.statusID} value={option.typeDescription}>
                      {option.typeDescription}
                    </option>
                  ))}
                </select>
              </InputGroup>
              <InputGroup>
                <label>Condition *</label>
                <select name="condition" value={materialInput.condition} onChange={handleMaterialChange}>
                  <option value="">Select condition</option>
                  {conditionOptions.map((option) => (
                    <option key={option.statusID} value={option.typeDescription}>
                      {option.typeDescription}
                    </option>
                  ))}
                </select>
              </InputGroup>
              <InputGroup>
                <label>Approx. Value</label>
                <input
                  type="text"
                  name="value"
                  value={materialInput.value}
                  onChange={(e) => {
                    const onlyNums = e.target.value.replace(/[^0-9.]/g, '');
                    setMaterialInput((prev) => ({ ...prev, value: onlyNums }));
                  }}
                />
              </InputGroup>
            </FormRow>
            <ButtonRow>
              <button className="cancel" onClick={() => setShowMaterialModal(false)}>Cancel</button>
              <button className="submit" onClick={handleSaveMaterial} disabled={isAddDisabled}
                style={{ opacity: isAddDisabled ? 0.5 : 1, cursor: isAddDisabled ? 'not-allowed' : 'pointer' }}>Add</button>
            </ButtonRow>
          </ModalContent>
        </ModalBackdrop>
      )}
      <ToastContainer />

    </Container>

  );
}
